(() => {
  'use strict';

  if (window.__HARDRIP_VERIFIED_SHOP_STATIC_V20204__) return;
  window.__HARDRIP_VERIFIED_SHOP_STATIC_V20204__ = true;

  const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const lower = (value) => clean(value).toLowerCase();
  const CARD_SELECTOR = '.jm-review-item,.jdgm-rev,[data-review-id]';
  const SHOP_SOURCE_SELECTOR = '[data-badge-type="review_written_in_shop_app"],.jdgm-rev__transparency-badge,[class*="transparency-badge" i],[class*="shop-badge" i],[class*="verified-shop" i],[class*="verified_by_shop" i]';
  const SHOP_TEXT = /review\s+written\s+in\s+shop\s+app|recensione\s+scritta\s+in\s+shop\s+app/i;
  const VERIFIED_TEXT = /verified\s*by\s*shop|verificat[oa]\s+(?:da|by)\s+shop/i;
  const BADGE_CLASS = /transparency[-_ ]?badge|verified[-_ ]?shop|verified_by_shop|shop[-_ ]?verified|shop[-_ ]?badge|review_written_in_shop_app/i;
  const MARK = 'data-hardrip-verified-shop-static-v20204';
  const SHOP_REVIEW_MARK = 'data-hardrip-shop-review-v20204';

  const signature = (node) => {
    if (!(node instanceof Element)) return '';
    const src = node instanceof HTMLImageElement
      ? `${node.currentSrc || ''} ${node.getAttribute('src') || ''} ${node.getAttribute('data-src') || ''} ${node.getAttribute('srcset') || ''}`
      : '';
    return clean(`${node.textContent || ''} ${node.id || ''} ${node.className || ''} ${node.getAttribute?.('data-badge-type') || ''} ${node.getAttribute?.('aria-label') || ''} ${node.getAttribute?.('title') || ''} ${node.getAttribute?.('alt') || ''} ${src}`);
  };

  const isShopReview = (card) => {
    if (!(card instanceof Element)) return false;
    if (card.getAttribute(SHOP_REVIEW_MARK) === 'true') return true;
    if (card.querySelector(SHOP_SOURCE_SELECTOR)) return true;
    if (SHOP_TEXT.test(clean(card.textContent || ''))) return true;
    return [...card.querySelectorAll('img,svg')].some((node) => VERIFIED_TEXT.test(signature(node)));
  };

  const semanticBadge = (node) => {
    if (!(node instanceof Element)) return false;
    if (node.matches?.(SHOP_SOURCE_SELECTOR) || node.closest?.(SHOP_SOURCE_SELECTOR)) return true;
    const sig = signature(node);
    if (VERIFIED_TEXT.test(sig)) return true;
    const cls = lower(`${node.id || ''} ${node.className || ''} ${node.getAttribute?.('data-badge-type') || ''}`);
    return BADGE_CLASS.test(cls);
  };

  const makeInert = (node) => {
    if (!(node instanceof Element)) return;
    node.setAttribute(MARK, 'true');
    node.setAttribute('tabindex', '-1');
    node.style.setProperty('cursor', 'default', 'important');
    node.style.setProperty('pointer-events', 'none', 'important');
    node.style.setProperty('-webkit-tap-highlight-color', 'transparent', 'important');
    node.removeAttribute('href');
    node.removeAttribute('onclick');
    node.removeAttribute('role');
    for (const attr of ['data-src', 'data-image', 'data-image-url', 'data-full', 'data-full-image', 'data-lightbox', 'data-gallery']) {
      if (node.hasAttribute(attr)) node.removeAttribute(attr);
    }
    if (node instanceof HTMLImageElement) {
      node.setAttribute('draggable', 'false');
      node.style.setProperty('-webkit-user-drag', 'none', 'important');
      node.style.setProperty('-webkit-touch-callout', 'none', 'important');
    }
  };

  const markBadge = (node, card) => {
    if (!(node instanceof Element) || !(card instanceof Element) || !card.contains(node)) return;
    makeInert(node);

    // Judge.me can wrap the official Shop badge in the same clickable media
    // component used for customer photos. Disable only the wrapper that owns
    // this badge, never the media list or genuine customer photographs.
    const wrapper = node.closest(
      'a,button,[role="button"],.jm-media-thumbnail-list__thumbnail,.jdgm-rev__pic-link,.jdgm-rev__pic,[class*="media-thumbnail" i],[class*="review-media-item" i],[class*="thumbnail" i]'
    );
    if (wrapper && wrapper !== card && card.contains(wrapper)) makeInert(wrapper);

    let parent = node.parentElement;
    let depth = 0;
    while (parent && parent !== card && depth < 3) {
      const sig = signature(parent);
      const containsOtherRealImages = [...parent.querySelectorAll('img')].some((img) => img !== node && !semanticBadge(img));
      if (containsOtherRealImages) break;
      if (semanticBadge(parent) || /button|link/.test(lower(parent.getAttribute('role') || '')) || parent.matches('a,button')) {
        makeInert(parent);
      }
      parent = parent.parentElement;
      depth += 1;
    }
  };

  const markCard = (card) => {
    if (!(card instanceof Element) || !isShopReview(card)) return;
    card.setAttribute(SHOP_REVIEW_MARK, 'true');

    card.querySelectorAll(SHOP_SOURCE_SELECTOR).forEach((node) => markBadge(node, card));
    card.querySelectorAll('img,svg,picture').forEach((node) => {
      if (semanticBadge(node)) markBadge(node, card);
    });
  };

  const scan = (scope = document) => {
    if (!scope?.querySelectorAll) return;
    if (scope instanceof Element && scope.matches(CARD_SELECTOR)) markCard(scope);
    scope.querySelectorAll(CARD_SELECTOR).forEach(markCard);
  };

  const badgeForEvent = (event) => {
    const path = typeof event?.composedPath === 'function' ? event.composedPath() : [event?.target];
    const elements = path.filter((node) => node instanceof Element);
    const target = event?.target instanceof Element ? event.target : null;

    for (const node of elements) {
      if (node.hasAttribute?.(MARK)) return node;
    }

    const card = target?.closest?.(CARD_SELECTOR) || elements.find((node) => node.matches?.(CARD_SELECTOR));
    if (!card || !isShopReview(card)) return null;

    for (const node of elements) {
      if (node === card) break;
      if (semanticBadge(node)) return node;
    }

    const image = target instanceof HTMLImageElement
      ? target
      : elements.find((node) => node instanceof HTMLImageElement) || target?.querySelector?.('img');
    if (image && card.contains(image) && semanticBadge(image)) return image;
    return null;
  };

  const stopBadgeInteraction = (event) => {
    if (!badgeForEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };

  // Capture at window + document so the official badge is stopped before any
  // Judge.me/HARDRIP gallery listener. The badge remains visible; only its
  // interaction is removed.
  for (const type of ['touchstart', 'touchend', 'pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click', 'auxclick']) {
    window.addEventListener(type, stopBadgeInteraction, { capture: true, passive: false });
    document.addEventListener(type, stopBadgeInteraction, { capture: true, passive: false });
  }

  const stopKeyboard = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!badgeForEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };
  window.addEventListener('keydown', stopKeyboard, true);
  document.addEventListener('keydown', stopKeyboard, true);

  let queued = false;
  const queueScan = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan(document);
    });
  };

  const start = () => {
    scan(document);
    const observer = new MutationObserver(queueScan);
    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['src', 'alt', 'title', 'class', 'data-badge-type']
    });
    [80, 220, 500, 1000, 1800, 3200, 6000, 10000].forEach((ms) => setTimeout(queueScan, ms));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
